import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../../contexts/SuperAdminAuthContext';
import { useSuperAdminTheme } from '../../../contexts/SuperAdminThemeContext';
import { usePendingApprovals } from '../../../contexts/PendingApprovalsContext';
import {
  LayoutDashboard,
  Building2,
  Users,
  Bot,
  Wallet,
  ShieldCheck,
  FileText,
  LogOut,
  Shield,
  User,
  Moon,
  Sun,
  Eye,
  ClipboardCheck,
  Activity,
  DollarSign,
  Gauge,
  BookOpen,
  Command as CommandIcon,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';

/**
 * Governance Sidebar — Iter 62.7 redesign.
 *
 * Linear-style: cool slate background, indigo accent on active state,
 * cmd-K hint, role-aware label, compact density.
 */
const links = [
  { label: 'Dashboard',     path: '/governance/dashboard',             icon: LayoutDashboard },
  { label: 'Unit Economics', path: '/governance/economics',            icon: DollarSign },
  { label: 'Autonomy & Margins', path: '/governance/autonomy-margins', icon: Bot },
  { label: 'Capabilities',  path: '/governance/capability',            icon: Gauge },
  { label: 'Founder Ops',   path: '/governance/founder-ops',           icon: Activity },
  { label: 'Approvals',     path: '/governance/approvals',             icon: ClipboardCheck },
  { label: 'Tenants',       path: '/governance/tenants',               icon: Users },
  { label: 'Organizations', path: '/governance/organizations',         icon: Building2 },
  { label: 'Users',         path: '/governance/users',                 icon: Users },
  { label: 'Agents',        path: '/governance/agents',                icon: Bot },
  { label: 'Budgets',       path: '/governance/budgets',               icon: Wallet },
  { label: 'Billing Drift',  path: '/governance/billing-drift',        icon: ShieldCheck },
  { label: 'Billing Audit',  path: '/governance/billing-audit',        icon: Wallet },
  { label: 'Usage Audit',    path: '/governance/usage-audit',          icon: Activity },
  { label: 'Confidence',    path: '/governance/confidence',            icon: ShieldCheck },
  { label: 'Audit Logs',    path: '/governance/audit',                 icon: FileText },
  { label: 'Service Subs',  path: '/governance/service-subscriptions', icon: Activity },
  { label: 'Help Insights', path: '/governance/help-insights',         icon: BookOpen },
  { label: 'Investor View', path: '/governance/investor',              icon: Eye },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useSuperAdminAuth();
  const { toggleTheme, isDark } = useSuperAdminTheme();
  const { pendingCount } = usePendingApprovals();

  const handleLogout = () => {
    logout();
    navigate('/superadmin/login', { replace: true });
  };

  // Role-aware label (Iter 62.6 retained)
  let adminType = 'Org Admin';
  if (user?.role === 'governor' || user?.is_governor || user?.scope === 'governance') {
    adminType = 'Governor';
  } else if (user?.is_platform_admin || (user?.role === 'superadmin' && !user?.org_id)) {
    adminType = 'Platform Admin';
  } else if (user?.role) {
    adminType = String(user.role).split(/[_\s-]+/).map(w => w ? w[0].toUpperCase() + w.slice(1) : '').join(' ');
  }

  return (
    <aside
      className="flex w-56 shrink-0 flex-col border-r border-[var(--gov-border)] bg-[var(--gov-bg-surface)]"
      data-testid="gov-sidebar"
    >
      {/* Brand */}
      <div className="flex items-center justify-between border-b border-[var(--gov-border)] px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--gov-accent-bg)] ring-1 ring-[var(--gov-accent-border)]">
            <Shield className="h-4 w-4 text-[var(--gov-accent)]" />
          </div>
          <div className="leading-tight">
            <div className="gov-text-primary text-[13px] font-semibold tracking-tight">Governance</div>
            <div className="gov-text-tertiary text-[10px] uppercase tracking-wider">{adminType}</div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="h-7 w-7 text-[var(--gov-text-tertiary)] hover:bg-[var(--gov-bg-elevated)] hover:text-[var(--gov-text-primary)]"
          data-testid="theme-toggle"
          title={isDark ? 'Light mode' : 'Dark mode'}
        >
          {isDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {/* ⌘K hint */}
      <div className="px-3 pt-3">
        <button
          onClick={() => {
            const ev = new KeyboardEvent('keydown', { key: 'k', metaKey: true });
            window.dispatchEvent(ev);
          }}
          className="group flex w-full items-center justify-between gap-2 rounded-md border border-[var(--gov-border)] bg-[var(--gov-bg-input)] px-2.5 py-1.5 text-left text-xs text-[var(--gov-text-tertiary)] transition-colors hover:border-[var(--gov-border-strong)] hover:text-[var(--gov-text-primary)]"
          data-testid="gov-sidebar-cmdk"
        >
          <span>Quick search…</span>
          <span className="inline-flex items-center gap-0.5 rounded border border-[var(--gov-border)] bg-[var(--gov-bg-surface)] px-1 py-0.5 text-[10px] font-mono">
            <CommandIcon className="h-2.5 w-2.5" />K
          </span>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3" data-testid="gov-sidebar-nav">
        {links.map(({ label, path, icon: Icon }) => {
          const isActive = location.pathname === path ||
            (path !== '/governance/dashboard' && location.pathname.startsWith(path));
          const showBadge = label === 'Approvals' && pendingCount > 0;
          return (
            <Link
              key={path}
              to={path}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                isActive
                  ? 'bg-[var(--gov-accent-bg)] text-[var(--gov-accent)] font-medium'
                  : 'text-[var(--gov-text-secondary)] hover:bg-[var(--gov-bg-elevated)] hover:text-[var(--gov-text-primary)]'
              }`}
            >
              <span className="flex items-center gap-2.5">
                <Icon className="h-4 w-4" strokeWidth={1.75} />
                {label}
              </span>
              {showBadge && (
                <Badge
                  variant="destructive"
                  className="h-4 min-w-[18px] rounded-full px-1 text-[10px] font-bold"
                  data-testid="pending-approvals-badge"
                >
                  {pendingCount > 99 ? '99+' : pendingCount}
                </Badge>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User info + logout */}
      <div className="border-t border-[var(--gov-border)] p-3">
        <div className="mb-2 flex items-center gap-2.5 rounded-md px-1 py-1">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--gov-bg-elevated)]">
            <User className="h-3.5 w-3.5 text-[var(--gov-text-tertiary)]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="gov-text-primary truncate text-xs font-medium">{user?.name || 'SuperAdmin'}</p>
            <p className="gov-text-tertiary truncate text-[10px]">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 text-xs text-[var(--gov-text-secondary)] hover:bg-[var(--gov-bg-elevated)] hover:text-[var(--gov-text-primary)]"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut className="h-3.5 w-3.5" />
          Logout
        </Button>
      </div>
    </aside>
  );
}
